import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import logo from "../src/assets/GON-logo1.png";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function Login() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpKey, setOtpKey] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [smsError, setSmsError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setSuccess("");
    setSmsError("");

    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }

    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-otp-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          phone: cleanPhone,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setOtpKey(data.otpKey);
        setStep(2);
        setTimer(300);
        setCanResend(false);
        setSmsError("");

        setUserInfo({
          name: name.trim(),
          phone: cleanPhone,
          type: "Staff",
        });

        setSuccess(
          `OTP sent successfully to ${cleanPhone}. Valid for 5 minutes.`
        );
      } else {
        const errorMsg = data.message || "Failed to send OTP";
        let detailedError = errorMsg;

        if (data.details && data.details.message) {
          detailedError = `${errorMsg}: ${data.details.message}`;
        }

        setError(detailedError);
        setSmsError(detailedError);
      }
    } catch (error) {
      console.error("Send OTP error:", error);
      setError("Unable to connect to server. Please check your connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (otp.length !== 6) {
      setError("Please enter the 6-digit OTP");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/verify-otp-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          otpKey,
          otp,
        }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        localStorage.setItem("userType", data.userType);
        localStorage.setItem("staffLevel", data.staffLevel || 1);

        setSuccess(data.message || "Login successful!");

        setTimeout(() => {
          // window.location.href = "/home";
          navigate("/");
        }, 1000);
      } else {
        const errorMsg = data.message || "Invalid OTP";
        let detailedError = errorMsg;

        if (data.details && data.details.server_error) {
          detailedError = `${errorMsg}: ${data.details.server_error}`;
        }

        setError(detailedError);
      }
    } catch (error) {
      console.error("Verify OTP error:", error);
      setError("Unable to verify OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setError("");
    setSuccess("");
    setSmsError("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/resend-otp-admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ otpKey }),
      });

      const data = await response.json();

      if (data.success) {
        setTimer(300);
        setCanResend(false);
        setSmsError("");
        setSuccess("New OTP sent successfully!");
      } else {
        const errorMsg = data.message || "Failed to resend OTP";
        let detailedError = errorMsg;

        if (data.details) {
          detailedError = `${errorMsg}: ${
            data.details.message || data.details.error || ""
          }`;
        }

        setError(detailedError);
        setSmsError(detailedError);

        if (data.message && data.message.includes("Maximum resend attempts")) {
          setTimeout(() => {
            handleBack();
          }, 3000);
        }
      }
    } catch (error) {
      console.error("Resend OTP error:", error);
      setError("Failed to resend OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp("");
    setError("");
    setSuccess("");
    setSmsError("");
    setTimer(0);
    setCanResend(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <div className="logo">
              <div className="logo-icon">
                <img src={logo} alt="Logo" className="logo-img" />
              </div>
              <div className="logo-text">
                <h1>SPEAR</h1>
                <p>Chief Secretary Office</p>
              </div>
            </div>
          </div>

          {/* {step === 1 && (
            <div className="login-type-selector">
              <div className="toggle-container">
                <button
                  type="button"
                  className="toggle-option active"
                  disabled={true}
                >
                  <div className="toggle-content">
                    <h3>Chief Secretary Office</h3> 
                    <p>Authorized Staff Members Only</p>
                  </div>
                  <div className="toggle-check">✓</div>
                </button>
              </div>
            </div>
          )} */}

          {step === 1 && (
            <form onSubmit={handleSendOTP} className="login-form">
              <div className="form-header">
                 <p style={{color:"red",fontSize:13}}>* Authorized Personnel Only.</p>
                {/* <h3>Staff Login</h3> */}
                {/* <p>Enter your details to receive OTP</p> */}
              </div>

              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input"
                  placeholder="Enter your full name as registered"
                  required
                  disabled={isLoading}
                />
                <div className="input-hint">
                  Must match exactly with registered name
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone Number
                </label>
                <div className="input-with-icon">
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={handlePhoneChange}
                    className="form-input"
                    placeholder="Enter 10-digit phone number"
                    inputMode="numeric"
                    required
                    disabled={isLoading}
                  />
                </div>
                <div className="phone-hint">
                  10-digit mobile number registered with your registered account
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">!</span>
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="success-message">
                  <span>{success}</span>
                </div>
              )}

              <button
                type="submit"
                className={`submit-button staff ${isLoading ? "loading" : ""}`}
                disabled={isLoading || !name.trim() || phone.length !== 10}
              >
                {isLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    Sending OTP...
                  </>
                ) : (
                  "Send OTP to Phone"
                )}
              </button>

              <div className="login-help">
                <p>Having trouble logging in?</p>
                <ul>
                  <li>Ensure you're using the exact name registered</li>
                  <li>Use the phone number linked to your staff account</li>
                  <li>Contact admin if you're unable to login</li>
                </ul>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerifyOTP} className="otp-form">
              <div className="otp-header">
                <button
                  type="button"
                  className="back-button"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  Back
                </button>
              </div>

              {/* {userInfo && (
                <div className="user-info-card">
                  <div className="user-info-header">
                    <div className="user-avatar">
                      {userInfo.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-details">
                      <h4>{userInfo.name}</h4>
                      <p className="user-phone">+91 ••••• •{userInfo.phone.slice(-4)}</p>
                      <p className="user-type">{userInfo.type} Account</p>
                    </div>
                  </div>
                </div>
              )} */}

              {smsError && (
                <div className="sms-warning">
                  <div className="sms-warning-icon">!</div>
                  <div className="sms-warning-content">
                    <strong>SMS Service Issue:</strong> {smsError}
                    <p className="sms-warning-note">
                      Please contact admin for assistance.
                    </p>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="otp" className="form-label">
                  6-digit Verification Code
                </label>
                <div className="otp-input-container">
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={handleOtpChange}
                    className="otp-input"
                    placeholder="Enter 6-digit OTP"
                    required
                    disabled={isLoading}
                    maxLength="6"
                    autoFocus
                  />
                </div>

                <div className="otp-actions">
                  <button
                    type="button"
                    className={`resend-button ${canResend ? "active" : ""}`}
                    onClick={handleResendOTP}
                    disabled={!canResend || isLoading}
                  >
                    {canResend
                      ? "Resend OTP"
                      : `Wait ${formatTime(timer)} to resend`}
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <span className="error-icon">!</span>
                  <span>{error}</span>
                </div>
              )}

              {success && (
                <div className="success-message">
                  <span className="success-icon">✓</span>
                  <span>{success}</span>
                </div>
              )}

              <button
                type="submit"
                className={`submit-button staff ${isLoading ? "loading" : ""}`}
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? (
                  <>
                    <span className="button-spinner"></span>
                    Verifying...
                  </>
                ) : (
                  "Verify & Login"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;
